import React, { useState, useEffect } from 'react';
import { apiWithAuth } from '../../lib/api';
import toast from 'react-hot-toast';
import { CurrencyYenIcon, TrashIcon, PhotoIcon } from '@heroicons/react/24/outline';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';

const AdminAdsPage = () => {
    const [slots, setSlots] = useState([]); // List of banners in carousel (max 10)
    const [candidates, setCandidates] = useState([]); // List of paid/manual banners waiting
    const [prices, setPrices] = useState({ banner: 10000, promo: 1000 });

    // Manual upload states
    const [newBannerImage, setNewBannerImage] = useState(null);
    const [newBannerLink, setNewBannerLink] = useState('');

    const fetchData = React.useCallback(async () => {
        try {
            const token = localStorage.getItem('token');
            const authApi = apiWithAuth(token);

            // Fetch Settings
            try {
                const sRes = await authApi.get('/admin/settings');
                if (sRes.status === 200) {
                    const settings = sRes.data;
                    const b = settings.find(k => k.key === 'banner_price_monthly');
                    const p = settings.find(k => k.key === 'promotion_price_daily');
                    setPrices({
                        banner: b ? parseInt(b.value) : 10000,
                        promo: p ? parseInt(p.value) : 1000
                    });
                }
            } catch (e) { console.error("Settings fetch failed", e); }

            // Fetch Slots and Candidates
            const [slotsRes, candRes] = await Promise.all([
                authApi.get('/admin/banners/slots'),
                authApi.get('/admin/banners/candidates')
            ]);

            if (slotsRes.status === 200) setSlots(slotsRes.data);
            if (candRes.status === 200) setCandidates(candRes.data);

        } catch (e) {
            console.error(e);
            toast.error('データの取得に失敗しました');
        }
    }, []);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleDragEnd = async (result) => {
        const { source, destination } = result;

        // Dropped outside
        if (!destination) return;

        // No move
        if (source.droppableId === destination.droppableId && source.index === destination.index) return;

        let newSlots = Array.from(slots);
        let newCandidates = Array.from(candidates);
        let isChanged = false;

        // Moving within Carousel Slots
        if (source.droppableId === 'slots' && destination.droppableId === 'slots') {
            const [reorderedItem] = newSlots.splice(source.index, 1);
            newSlots.splice(destination.index, 0, reorderedItem);
            setSlots(newSlots);
            isChanged = true;
        }
        // Moving from Candidates to Slots
        else if (source.droppableId === 'candidates' && destination.droppableId === 'slots') {
            if (newSlots.length >= 10) {
                toast.error('カルーセル枠は最大10件までです');
                return;
            }
            const [movedItem] = newCandidates.splice(source.index, 1);
            newSlots.splice(destination.index, 0, movedItem);
            setSlots(newSlots);
            setCandidates(newCandidates);
            isChanged = true;
        }
        // Moving from Slots to Candidates (Remove from Carousel)
        else if (source.droppableId === 'slots' && destination.droppableId === 'candidates') {
            const [movedItem] = newSlots.splice(source.index, 1);
            newCandidates.splice(destination.index, 0, movedItem);
            setSlots(newSlots);
            setCandidates(newCandidates);
            isChanged = true;
        }

        if (isChanged) {
            await saveOrder(newSlots);
        }
    };

    const saveOrder = async (orderedSlots) => {
        try {
            const token = localStorage.getItem('token');
            const authApi = apiWithAuth(token);
            await authApi.put('/admin/banners/order', {
                ordered_ids: orderedSlots.map(s => s.id)
            });
            toast.success('表示順を更新しました');
            // Optimistically updated UI already
        } catch (e) {
            console.error(e);
            toast.error('更新に失敗しました');
            fetchData(); // Rollback on error
        }
    };

    const handleManualUpload = async () => {
        if (!newBannerImage || !newBannerLink) {
            toast.error("画像とリンクを入力してください");
            return;
        }
        try {
            const token = localStorage.getItem('token');
            const authApi = apiWithAuth(token);
            await authApi.post('/admin/banners', {
                image_base64: newBannerImage,
                link_url: newBannerLink,
                description: "Manual Admin Upload",
                months: 12
            });
            toast.success('バナーを追加しました');
            setNewBannerImage(null);
            setNewBannerLink('');
            fetchData();
        } catch (e) {
            toast.error('追加失敗');
        }
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => setNewBannerImage(reader.result);
            reader.readAsDataURL(file);
        }
    };

    const deleteBanner = async (id) => {
        if (!window.confirm('このバナーを完全に削除しますか？')) return;
        try {
            const token = localStorage.getItem('token');
            const authApi = apiWithAuth(token);
            await authApi.delete(`/banners/${id}`);
            // Reuse public delete? Usually separate route or check permissions
            // Assuming generic delete works for admin or add to routes_ads

            toast.success('削除しました');
            fetchData();
        } catch (e) {
            toast.error('削除失敗');
        }
    };

    const updatePrice = async (key, value) => {
        try {
            const token = localStorage.getItem('token');
            const authApi = apiWithAuth(token);
            await authApi.post(`/admin/settings/${key}`, {
                value: String(value),
                description: key,
                category: 'ads'
            });
            toast.success('価格を更新しました');
        } catch (e) { toast.error('更新失敗'); }
    };

    return (
        <div className="p-6 max-w-7xl mx-auto bg-gray-50 min-h-screen">
            <h1 className="text-3xl font-bold mb-8 text-gray-900">広告・マーケティング管理</h1>

            {/* Price Settings */}
            <div className="bg-white rounded-xl shadow-sm p-6 mb-8">
                <h2 className="text-xl font-bold mb-4 flex items-center">
                    <CurrencyYenIcon className="w-6 h-6 mr-2 text-gray-500" />
                    価格設定
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">バナー広告 (月額)</label>
                        <div className="flex items-center">
                            <input
                                type="number"
                                className="border rounded-lg px-3 py-2 w-full text-lg"
                                value={prices.banner}
                                onChange={e => setPrices({ ...prices, banner: e.target.value })}
                                onBlur={() => updatePrice('banner_price_monthly', prices.banner)}
                            />
                            <span className="ml-2 text-gray-500">円</span>
                        </div>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">おすすめ表示 (日額)</label>
                        <div className="flex items-center">
                            <input
                                type="number"
                                className="border rounded-lg px-3 py-2 w-full text-lg"
                                value={prices.promo}
                                onChange={e => setPrices({ ...prices, promo: e.target.value })}
                                onBlur={() => updatePrice('promotion_price_daily', prices.promo)}
                            />
                            <span className="ml-2 text-gray-500">円</span>
                        </div>
                    </div>
                </div>
            </div>

            <DragDropContext onDragEnd={handleDragEnd}>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    {/* Left: Carousel Slots */}
                    <div className="bg-white rounded-xl shadow-sm p-6 border-2 border-dashed border-blue-200 min-h-[500px]">
                        <h2 className="text-xl font-bold mb-2 text-blue-800">公開中カルーセル ({slots.length}/10)</h2>
                        <p className="text-sm text-gray-500 mb-4">
                            ここに配置されたバナーがトップページに表示されます。上から順番に表示されます。
                        </p>

                        <Droppable droppableId="slots">
                            {(provided) => (
                                <div
                                    {...provided.droppableProps}
                                    ref={provided.innerRef}
                                    className="space-y-3 min-h-[400px]"
                                >
                                    {slots.map((banner, index) => (
                                        <Draggable key={banner.id} draggableId={banner.id} index={index}>
                                            {(provided) => (
                                                <div
                                                    ref={provided.innerRef}
                                                    {...provided.draggableProps}
                                                    {...provided.dragHandleProps}
                                                    className="bg-white border rounded-lg p-3 shadow-sm flex items-center hover:shadow-md transition-shadow relative group"
                                                >
                                                    <span className="bg-blue-100 text-blue-800 font-bold px-2 py-1 rounded mr-3 text-sm">#{index + 1}</span>
                                                    <img src={banner.image_url} alt="" className="w-16 h-10 object-cover rounded mr-3" />
                                                    <div className="flex-1 min-w-0">
                                                        <p className="text-sm font-medium truncate">{banner.link_url}</p>
                                                        <p className="text-xs text-gray-500">期限: {new Date(banner.expires_at).toLocaleDateString()}</p>
                                                    </div>
                                                </div>
                                            )}
                                        </Draggable>
                                    ))}
                                    {provided.placeholder}
                                    {slots.length === 0 && (
                                        <div className="text-center py-12 text-gray-400">
                                            候補リストからドラッグして追加してください
                                        </div>
                                    )}
                                </div>
                            )}
                        </Droppable>
                    </div>

                    {/* Right: Candidates & Manual Add */}
                    <div className="space-y-8">
                        {/* Manual Add Form */}
                        <div className="bg-white rounded-xl shadow-sm p-6">
                            <h2 className="text-lg font-bold mb-4 flex items-center">
                                <PhotoIcon className="w-5 h-5 mr-2" />
                                新規バナー追加 (自社広告など)
                            </h2>
                            <div className="flex space-x-4 mb-4">
                                <div className="flex-1">
                                    <input type="file" onChange={handleImageChange} className="text-sm" />
                                </div>
                                <div className="flex-1">
                                    <input
                                        type="text"
                                        placeholder="リンクURL"
                                        className="border rounded px-2 py-1 w-full"
                                        value={newBannerLink}
                                        onChange={e => setNewBannerLink(e.target.value)}
                                    />
                                </div>
                                <button
                                    onClick={handleManualUpload}
                                    className="bg-blue-600 text-white px-4 py-1 rounded hover:bg-blue-700 text-sm"
                                >
                                    追加
                                </button>
                            </div>
                        </div>

                        {/* Candidates List */}
                        <div className="bg-gray-100 rounded-xl p-6 min-h-[300px]">
                            <h2 className="text-xl font-bold mb-2 text-gray-800">掲載候補リスト</h2>
                            <p className="text-sm text-gray-500 mb-4">
                                事業者が購入したバナーや手動追加したバナーがここに溜まります。
                            </p>

                            <Droppable droppableId="candidates">
                                {(provided) => (
                                    <div
                                        {...provided.droppableProps}
                                        ref={provided.innerRef}
                                        className="space-y-3"
                                    >
                                        {candidates.map((banner, index) => (
                                            <Draggable key={banner.id} draggableId={banner.id} index={index}>
                                                {(provided) => (
                                                    <div
                                                        ref={provided.innerRef}
                                                        {...provided.draggableProps}
                                                        {...provided.dragHandleProps}
                                                        className="bg-white border rounded-lg p-3 flex items-center relative group opacity-90 hover:opacity-100"
                                                    >
                                                        <img src={banner.image_url} alt="" className="w-16 h-10 object-cover rounded mr-3 grayscale" />
                                                        <div className="flex-1 min-w-0">
                                                            <p className="text-sm font-medium truncate text-gray-600">{banner.link_url}</p>
                                                            <p className="text-xs text-gray-500">期限: {new Date(banner.expires_at).toLocaleDateString()}</p>
                                                        </div>
                                                        <button
                                                            className="p-1 text-red-400 hover:text-red-600"
                                                            onClick={(e) => { e.stopPropagation(); deleteBanner(banner.id); }}
                                                        >
                                                            <TrashIcon className="w-4 h-4" />
                                                        </button>
                                                    </div>
                                                )}
                                            </Draggable>
                                        ))}
                                        {provided.placeholder}
                                        {candidates.length === 0 && (
                                            <div className="text-center py-8 text-gray-400">
                                                候補はありません
                                            </div>
                                        )}
                                    </div>
                                )}
                            </Droppable>
                        </div>
                    </div>
                </div>
            </DragDropContext>
        </div>
    );
};

export default AdminAdsPage;
